import { useState, FC, useCallback, ReactNode } from "react"
import dayjs, { Dayjs } from "utils/dayjs"

import toastMessages from "stores/toastMessages"
import { toDateTimeWithoutTimezone } from "utils/utils"
import DateTimeModal, { DateTimeType, IDateTimeModalProps } from "components/DateTimeModal/DateTimeModal"

export interface IDateTimeControlProps extends Omit<IDateTimeModalProps, "onClose" | "onChange"> {
    type?: DateTimeType
    date?: Dayjs | null
    utc?: boolean
    onDateChange?: (date: Dayjs | null) => Promise<any> | any
    testIdPrefix?: string
    children(params: {
        openDateTimeModal(): void
    }): ReactNode
}

const DateTimeControl: FC<IDateTimeControlProps> = ({
    type = "date",
    date = null,
    utc,
    testIdPrefix = "date-time-control",
    onDateChange = () => {},
    children,

    minDate,
    maxDate,
    minDetail,
    maxDetail,
    defaultActiveStartDate,
    minutesStep,
    defaultHours,
    defaultMinutes,
    noSaveButton,
}) => {
    const getDate = useCallback((dt?: Dayjs | null) => {
        if (!dt) return null
        return utc ? dayjs(toDateTimeWithoutTimezone(dt)).utc() : dt?.clone().local()
    }, [utc])

    const [showDateTimeModal, setShowDateTimeModal] = useState(false)

    async function applyDate(dateToApply: Dayjs | null) {
        try {
            await onDateChange(getDate(dateToApply))
        } catch (e) {
            toastMessages.showSystemError()
        }
    }

    function openDateTimeModal() {
        setShowDateTimeModal(true)
    }

    function closeDateTimeModal() {
        setShowDateTimeModal(false)
    }

    return (
        <>
            {children({
                openDateTimeModal,
            })}
            {showDateTimeModal &&
                <DateTimeModal
                    type={type}
                    date={date}
                    minDate={minDate}
                    maxDate={maxDate}
                    minDetail={minDetail}
                    maxDetail={maxDetail}
                    defaultActiveStartDate={defaultActiveStartDate}
                    minutesStep={minutesStep}
                    defaultHours={defaultHours}
                    defaultMinutes={defaultMinutes}
                    noSaveButton={noSaveButton}
                    utc={utc}
                    testIdPrefix={testIdPrefix}
                    onChange={applyDate}
                    onClose={closeDateTimeModal}
                />
            }
        </>
    )
}

export default DateTimeControl
